import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { HeartHandshake, MessageCircle, Hand, Sparkles, ArrowRight, CheckCircle, History } from "lucide-react";
import ExerciseIntro from "../components/ExerciseIntro";
import { getExerciseInfo } from "../lib/exerciseData";
import { useI18n } from "../lib/i18n";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";
import { API } from "../App";

const SelfCompassionPage = ({ user, logout }) => {
  const { t } = useI18n();
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const EXERCISE_INFO = getExerciseInfo(t);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [responses, setResponses] = useState({});
  const [completedPhrases, setCompletedPhrases] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [entries, setEntries] = useState([]);

  useEffect(() => { fetchEntries(); }, []);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/self-compassion`, { withCredentials: true });
      setEntries(res.data);
    } catch (e) { console.error(e); }
  };

  const saveSession = async (exercise) => {
    try {
      const res = await axios.post(`${API}/self-compassion`, {
        exercise_type: exercise.id,
        responses: responses,
        completed_phrases: completedPhrases,
      }, { withCredentials: true });
      toast.success(t("compassion.completed"));
      if (user?.role === "patient") promptShare(res.data, "self_compassion");
      fetchEntries();
    } catch (e) { console.error(e); }
  };

  const SELF_COMPASSION_EXERCISES = [
    {
      id: "letter",
      name: t("compassion.letter"),
      description: t("compassion.letterDesc"),
      icon: MessageCircle,
      prompts: [t("compassion.letter.prompt1"), t("compassion.letter.prompt2"), t("compassion.letter.prompt3")],
    },
    {
      id: "touch",
      name: t("compassion.touch"),
      description: t("compassion.touchDesc"),
      icon: Hand,
      steps: [
        t("compassion.touch.step1"), t("compassion.touch.step2"), t("compassion.touch.step3"),
        t("compassion.touch.step4"), t("compassion.touch.step5"), t("compassion.touch.step6"),
      ],
    },
    {
      id: "phrases",
      name: t("compassion.phrases"),
      description: t("compassion.phrasesDesc"),
      icon: Sparkles,
      phrases: [
        t("compassion.phrase1"), t("compassion.phrase2"), t("compassion.phrase3"),
        t("compassion.phrase4"), t("compassion.phrase5"), t("compassion.phrase6"),
      ],
    },
  ];

  const handleResponseChange = (promptIndex, value) => {
    setResponses(prev => ({ ...prev, [promptIndex]: value }));
  };

  const togglePhrase = (phrase) => {
    setCompletedPhrases(prev =>
      prev.includes(phrase) ? prev.filter(p => p !== phrase) : [...prev, phrase]
    );
  };

  const closeExercise = (save = false) => {
    if (save && selectedExercise) saveSession(selectedExercise);
    setSelectedExercise(null);
    setResponses({});
    setCompletedPhrases([]);
    setCurrentStep(0);
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="self-compassion-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("compassion.title")}</h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("compassion.pageSubtitle")}</p>
          </div>
          {entries.length > 0 && (
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <History className="w-4 h-4" /> {entries.length} {t("compassion.sessionsCompleted")}
            </span>
          )}
        </div>

        <ExerciseIntro exerciseInfo={EXERCISE_INFO.self_compassion} />

        {selectedExercise ? (
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <selectedExercise.icon className="w-5 h-5" />
                {selectedExercise.name}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-muted-foreground">{selectedExercise.description}</p>

              {selectedExercise.id === "letter" && (
                <div className="space-y-6">
                  {selectedExercise.prompts.map((prompt, idx) => (
                    <div key={idx} className="space-y-2">
                      <label className="text-sm font-medium">{prompt}</label>
                      <Textarea
                        placeholder={t("compassion.writeHere")}
                        value={responses[idx] || ""}
                        onChange={(e) => handleResponseChange(idx, e.target.value)}
                        rows={4}
                        data-testid={`compassion-prompt-${idx}`}
                      />
                    </div>
                  ))}
                  <Button onClick={() => closeExercise(true)} className="w-full bg-primary hover:bg-primary/90" data-testid="finish-letter-btn">
                    {t("compassion.finishExercise")}
                  </Button>
                </div>
              )}

              {selectedExercise.id === "touch" && (
                <div className="space-y-6">
                  <ol className="space-y-4">
                    {selectedExercise.steps.map((step, idx) => (
                      <li
                        key={idx}
                        className={`flex gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
                          currentStep >= idx ? "bg-pink-50" : "bg-muted/50"
                        }`}
                        onClick={() => setCurrentStep(idx)}
                      >
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm flex-shrink-0 ${
                          currentStep >= idx ? "bg-pink-500 text-white" : "bg-muted text-muted-foreground"
                        }`}>
                          {currentStep > idx ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                        </span>
                        <span className={currentStep >= idx ? "text-foreground" : "text-muted-foreground"}>
                          {step}
                        </span>
                      </li>
                    ))}
                  </ol>
                  <Button
                    onClick={() =>
                      currentStep < selectedExercise.steps.length - 1
                        ? setCurrentStep(currentStep + 1)
                        : closeExercise(true)
                    }
                    className="w-full bg-primary hover:bg-primary/90"
                    data-testid="next-step-btn"
                  >
                    {currentStep < selectedExercise.steps.length - 1 ? (
                      <>
                        {t("compassion.nextStep")}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    ) : (
                      t("compassion.finishExercise")
                    )}
                  </Button>
                </div>
              )}

              {selectedExercise.id === "phrases" && (
                <div className="space-y-6">
                  <p className="text-sm text-muted-foreground">{t("compassion.repeatInstruction")}</p>
                  <div className="space-y-3">
                    {selectedExercise.phrases.map((phrase, idx) => (
                      <button
                        key={idx}
                        onClick={() => togglePhrase(phrase)}
                        className={`w-full p-4 rounded-lg text-left transition-all border ${
                          completedPhrases.includes(phrase)
                            ? "bg-pink-50 border-pink-200 text-pink-800"
                            : "bg-background border-border hover:bg-muted"
                        }`}
                        data-testid={`phrase-${idx}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                            completedPhrases.includes(phrase)
                              ? "bg-pink-500 text-white"
                              : "border border-muted-foreground/30"
                          }`}>
                            {completedPhrases.includes(phrase) && <CheckCircle className="w-4 h-4" />}
                          </div>
                          <span className={completedPhrases.includes(phrase) ? "line-through opacity-60" : ""}>
                            {phrase}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                  <Button
                    onClick={() => closeExercise(true)}
                    className="w-full bg-primary hover:bg-primary/90"
                    disabled={completedPhrases.length === 0}
                    data-testid="finish-phrases-btn"
                  >
                    {t("compassion.finishExercise")}
                  </Button>
                </div>
              )}

              <Button variant="ghost" onClick={() => closeExercise(false)}>
                {t("compassion.backToExercises")}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-3 gap-6">
            {SELF_COMPASSION_EXERCISES.map((exercise) => (
              <Card
                key={exercise.id}
                className="card-hover cursor-pointer"
                onClick={() => setSelectedExercise(exercise)}
                data-testid={`compassion-card-${exercise.id}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-pink-50 text-pink-600 flex items-center justify-center mb-4">
                    <exercise.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold mb-2">{exercise.name}</h3>
                  <p className="text-sm text-muted-foreground">{exercise.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

export default SelfCompassionPage;
